import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Printer, Link2, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { BillingDocument } from '../components/BillingDocument';
import { api, errMsg, copyToClipboard } from '../lib/api';
import { peso } from '../lib/format';

export default function BillingPage() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pay, setPay] = useState({ method: 'cash', reference: '', amount: '', or_number: '', notes: '' });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/jobs/${jobId}`);
      setDetail(data);
      const total = data.billing_statement?.snapshot_json?.total_centavos ?? data.total_centavos;
      const remaining = Math.max(0, total - data.paid_centavos);
      setPay((p) => ({ ...p, amount: (remaining / 100).toFixed(2) }));
    } catch (e) {
      toast.error(errMsg(e));
    }
  }, [jobId]);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/jobs/${jobId}/billing-statement`);
      toast.success(data.message);
      await load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const copyPublicLink = async () => {
    const url = `${window.location.origin}/b/${detail.billing_statement.public_token}`;
    const ok = await copyToClipboard(url);
    toast.success(ok ? 'Link copied — paste it into your chat with the customer.' : url);
  };

  const recordPayment = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/jobs/${jobId}/payments`, {
        method: pay.method,
        reference: pay.reference || null,
        amount_centavos: Math.round(parseFloat(pay.amount) * 100),
        or_number: pay.or_number,
        notes: pay.notes || null,
      });
      toast.success(data.message);
      await load();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  if (!detail) {
    return <div className="space-y-3"><Skeleton className="h-64 rounded-2xl" /><Skeleton className="h-40 rounded-2xl" /></div>;
  }

  const bs = detail.billing_statement;
  const job = detail.job;
  const paid = detail.paid_centavos;
  const total = bs?.snapshot_json?.total_centavos ?? detail.total_centavos;
  const isClosed = job.status === 'closed';
  const lastOr = detail.payments.length ? detail.payments[detail.payments.length - 1].or_number : null;

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      <div className="no-print flex items-center justify-between gap-2 flex-wrap">
        <Button data-testid="billing-back-button" variant="ghost" className="rounded-xl" onClick={() => navigate(`/jobs/${jobId}`)}>
          <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to job
        </Button>
        {bs && (
          <div className="flex gap-2">
            <Button data-testid="billing-copy-link-button" variant="outline" className="h-10 rounded-xl" onClick={copyPublicLink}>
              <Link2 className="h-4 w-4 mr-1.5" /> Copy link
            </Button>
            <Button data-testid="billing-print-button" variant="outline" className="h-10 rounded-xl" onClick={() => window.print()}>
              <Printer className="h-4 w-4 mr-1.5" /> Print
            </Button>
          </div>
        )}
      </div>

      {!bs ? (
        <Card className="rounded-2xl border-black/5 shadow-[var(--shadow-soft)]">
          <CardContent className="pt-6 pb-6 text-center space-y-3">
            <FileText className="h-8 w-8 mx-auto text-[hsl(var(--muted-foreground))]" />
            <p className="text-sm text-[hsl(var(--muted-foreground))]">Generate the billing statement to freeze the line items and get a BS number.</p>
            <Button data-testid="billing-generate-button" disabled={busy || job.status !== 'ready'} onClick={generate} className="h-12 rounded-xl px-6">
              {busy ? 'Generating…' : 'Generate billing statement'}
            </Button>
            {job.status !== 'ready' && <p className="text-xs text-[hsl(var(--muted-foreground))]">Available when the job is ready for release.</p>}
          </CardContent>
        </Card>
      ) : (
        <>
          <BillingDocument
            bsNumber={bs.bs_number}
            snapshot={bs.snapshot_json}
            createdAt={bs.created_at}
            paid={isClosed}
            orNumber={lastOr}
          />

          {!isClosed && (
            <Card className="no-print rounded-2xl border-black/5 shadow-[var(--shadow-soft)]">
              <CardContent className="pt-5">
                <h2 className="text-base font-semibold font-display mb-1">Record payment</h2>
                <p className="text-xs text-[hsl(var(--muted-foreground))] mb-3">
                  {paid > 0 ? `${peso(paid)} of ${peso(total)} recorded so far. ` : ''}
                  The job closes when recorded payments reach the total.
                </p>
                <form onSubmit={recordPayment} className="space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Select value={pay.method} onValueChange={(v) => setPay({ ...pay, method: v })}>
                      <SelectTrigger data-testid="billing-payment-method-select" className="h-11 rounded-xl"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cash">Cash</SelectItem>
                        <SelectItem value="gcash_manual">GCash (manual)</SelectItem>
                        <SelectItem value="bank_transfer">Bank transfer</SelectItem>
                        <SelectItem value="card_manual">Card (manual)</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      data-testid="billing-payment-reference-input"
                      placeholder={pay.method === 'cash' ? 'Reference (optional)' : 'Reference number (required)'}
                      required={pay.method !== 'cash'}
                      value={pay.reference}
                      onChange={(e) => setPay({ ...pay, reference: e.target.value })}
                      className="h-11 rounded-xl"
                    />
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[hsl(var(--muted-foreground))]">₱</span>
                      <Input data-testid="billing-payment-amount-input" type="number" step="0.01" min="0.01" required placeholder="Amount" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} className="h-11 rounded-xl pl-7" />
                    </div>
                    <Input data-testid="billing-payment-or-input" required placeholder="OR number (from your BIR booklet)" value={pay.or_number} onChange={(e) => setPay({ ...pay, or_number: e.target.value })} className="h-11 rounded-xl" />
                  </div>
                  <Input data-testid="billing-payment-notes-input" placeholder="Notes (optional)" value={pay.notes} onChange={(e) => setPay({ ...pay, notes: e.target.value })} className="h-11 rounded-xl" />
                  <Button data-testid="billing-record-payment-button" type="submit" disabled={busy} className="w-full h-12 rounded-xl">
                    {busy ? 'Recording…' : 'Record payment'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}

          {detail.payments.length > 0 && (
            <Card className="no-print rounded-2xl border-black/5 shadow-[var(--shadow-soft)]">
              <CardContent className="pt-5 space-y-2">
                <h2 className="text-base font-semibold font-display">Recorded payments</h2>
                {detail.payments.map((p) => (
                  <div key={p.id} className="flex items-center justify-between text-sm">
                    <span data-testid="billing-or-number">OR {p.or_number} · {p.method.replace('_', ' ')}{p.reference ? ` · ${p.reference}` : ''}</span>
                    <span className="font-semibold tabular-nums">{peso(p.amount_centavos)}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
